import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  X,
  ArrowUp,
  Wallet,
  Receipt,
  CheckCircle,
  AlertCircle,
  Calendar,
  MapPin,
  Plus,
  ChevronRight,
  Download,
  FileText,
  Info,
  Maximize2,
  Calculator,
} from "lucide-react";
import { useAppContext } from "../../../AppContext";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";

export default function AccountantContractorExpenses({
  contractorId,
  contractorName,
  contractorData,
}) {
  const { t } = useTranslation();
  const { Lang, AllData } = useAppContext();
  const isRTL = Lang === "ar" || Lang === "he";

  const [expenses, setExpenses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [places, setPlaces] = useState([]);
  const [activeTab, setActiveTab] = useState("invoices");
  const [searchQuery, setSearchQuery] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    type: "info",
    image: null,
  });
  const [exportModalVisible, setExportModalVisible] = useState(false);

  const containerRef = useRef(null);

  useEffect(() => {
    if (AllData) {
      setExpenses(AllData.myExpenses || []);
      setInvoices(AllData.myInvoices || []);
      setPlaces(AllData.myPlaces || []);
    }
  }, [AllData]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const [day, month, year] = dateString.split("-");
    const date = new Date(`${year}-${month}-${day}`);
    return date.toLocaleDateString(Lang === "ar" ? "ar-EG" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "0.00";
    return new Intl.NumberFormat(Lang === "ar" ? "ar-EG" : "en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getPlaceName = (placeId) => {
    const place = places.find((p) => p.id === placeId);
    return place ? place.place : t("unknown");
  };

  const showModal = (title, message, type = "info", image = null) => {
    setModalConfig({ title, message, type, image });
    setModalVisible(true);
  };

  const getTotalExpenses = () => {
    return expenses.reduce((sum, expense) => sum + (expense.price || 0), 0);
  };

  const getTotalInvoices = () => {
    return invoices.reduce(
      (sum, invoice) => sum + (parseFloat(invoice.totalAmount) || 0),
      0
    );
  };

  const getTotalSubTotal = () => {
    return invoices.reduce(
      (sum, invoice) => sum + (parseFloat(invoice.subTotal) || 0),
      0
    );
  };

  const getTotalTax = () => {
    return invoices.reduce(
      (sum, invoice) => sum + (parseFloat(invoice.taxAmount) || 0),
      0
    );
  };

  const getExpensesWithInvoice = () => {
    return expenses.filter((e) => e.withInvoice === 1).length;
  };

  const getExpensesWithoutInvoice = () => {
    return expenses.filter((e) => e.withInvoice === 2).length;
  };

  const getFilteredInvoices = () => {
    if (!searchQuery.trim()) {
      return invoices;
    }
    return invoices.filter(
      (invoice) =>
        invoice.inoviceNumber
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        invoice.vendorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        invoice.businessNumber
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase())
    );
  };

  const handleScroll = (event) => {
    const offsetY = event.target.scrollTop;
    setShowScrollTop(offsetY > 300);
  };

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      setSelectedInvoices([]);
    }
  };

  const toggleInvoiceSelection = (invoiceId) => {
    if (selectedInvoices.includes(invoiceId)) {
      setSelectedInvoices(selectedInvoices.filter((id) => id !== invoiceId));
    } else {
      setSelectedInvoices([...selectedInvoices, invoiceId]);
    }
  };

  const selectAllInvoices = () => {
    const filtered = getFilteredInvoices();
    setSelectedInvoices(filtered.map((inv) => inv.id));
  };

  const deselectAllInvoices = () => {
    setSelectedInvoices([]);
  };

  const exportToExcel = async () => {
    try {
      const dataToExport =
        selectedInvoices.length > 0
          ? invoices.filter((inv) => selectedInvoices.includes(inv.id))
          : getFilteredInvoices();

      if (dataToExport.length === 0) {
        showModal(t("noData"), t("noInvoicesToExport"), "warning");
        return;
      }

      const worksheetData = [
        [
          t("invoiceNumber"),
          t("vendorName"),
          t("businessNumber"),
          t("invoiceDate"),
          t("place"),
          t("subTotal"),
          t("taxRate"),
          t("taxAmount"),
          t("totalAmount"),
        ],
        ...dataToExport.map((invoice) => [
          invoice.inoviceNumber || "",
          invoice.vendorName || "",
          invoice.businessNumber || "",
          invoice.inoviceDate || "",
          getPlaceName(invoice.placeId),
          invoice.subTotal || "0",
          invoice.taxRate || "0",
          invoice.taxAmount || "0",
          invoice.totalAmount || "0",
        ]),
        [],
        [
          t("totals"),
          "",
          "",
          "",
          "",
          dataToExport
            .reduce((sum, inv) => sum + (parseFloat(inv.subTotal) || 0), 0)
            .toFixed(2),
          "",
          dataToExport
            .reduce((sum, inv) => sum + (parseFloat(inv.taxAmount) || 0), 0)
            .toFixed(2),
          dataToExport
            .reduce((sum, inv) => sum + (parseFloat(inv.totalAmount) || 0), 0)
            .toFixed(2),
        ],
      ];

      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Invoices");

      XLSX.writeFile(wb, `invoices_${contractorName}_${Date.now()}.xlsx`);

      showModal(t("success"), t("exportedSuccessfully"), "success");
      setSelectionMode(false);
      setSelectedInvoices([]);
    } catch (error) {
      console.error("Export error:", error);
      showModal(t("error"), t("exportFailed"), "error");
    }
  };

  const exportToPDF = () => {
    const dataToExport =
      selectedInvoices.length > 0
        ? invoices.filter((inv) => selectedInvoices.includes(inv.id))
        : getFilteredInvoices();

    if (dataToExport.length === 0) {
      showModal(t("noData"), t("noInvoicesToExport"), "warning");
      return;
    }

    const htmlContent = `
<!DOCTYPE html>
<html dir="${isRTL ? "rtl" : "ltr"}">
<head>
  <meta charset="utf-8">
  <title>${t("invoices")} - ${contractorName}</title>
  <style>
    @media print {
      @page {
        size: A4 landscape;
        margin: 15mm;
      }
      body {
        margin: 0;
        padding: 0;
      }
    }
    
    body {
      font-family: Arial, sans-serif;
      padding: 20px;
      direction: ${isRTL ? "rtl" : "ltr"};
      background: white;
    }
    
    h1 {
      color: #3B82F6;
      text-align: center;
      margin-bottom: 10px;
      font-size: 24px;
    }
    
    .header-info {
      text-align: center;
      color: #666;
      margin-bottom: 20px;
      font-size: 14px;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 20px;
      font-size: 12px;
    }
    
    th, td {
      border: 1px solid #ddd;
      padding: 10px;
      text-align: ${isRTL ? "right" : "left"};
    }
    
    th {
      background-color: #3B82F6;
      color: white;
      font-weight: bold;
    }
    
    tr:nth-child(even) {
      background-color: #f9fafb;
    }
    
    .totals {
      background-color: #EFF6FF !important;
      font-weight: bold;
      color: #000;
    }
    
    .footer {
      margin-top: 30px;
      text-align: center;
      color: #999;
      font-size: 11px;
    }
    
    .number-cell {
      text-align: ${isRTL ? "left" : "right"};
    }
    
    @media print {
      .no-print {
        display: none;
      }
    }
  </style>
</head>
<body>
  <h1>${t("invoices")} - ${contractorName}</h1>
  <div class="header-info">
    ${t("date")}: ${new Date().toLocaleDateString(isRTL ? "he-IL" : "en-US")}
  </div>
  
  <table>
    <thead>
      <tr>
        <th>${t("invoiceNumber")}</th>
        <th>${t("vendorName")}</th>
        <th>${t("invoiceDate")}</th>
        <th>${t("place")}</th>
        <th class="number-cell">${t("subTotal")}</th>
        <th class="number-cell">${t("tax")}</th>
        <th class="number-cell">${t("totalAmount")}</th>
      </tr>
    </thead>
    <tbody>
      ${dataToExport
        .map(
          (invoice) => `
        <tr>
          <td>${invoice.inoviceNumber || ""}</td>
          <td>${invoice.vendorName || ""}</td>
          <td>${invoice.inoviceDate || ""}</td>
          <td>${getPlaceName(invoice.placeId)}</td>
          <td class="number-cell">₪${formatCurrency(invoice.subTotal)}</td>
          <td class="number-cell">₪${formatCurrency(invoice.taxAmount)}</td>
          <td class="number-cell">₪${formatCurrency(invoice.totalAmount)}</td>
        </tr>
      `
        )
        .join("")}
      <tr class="totals">
        <td colspan="4">${t("totals")}</td>
        <td class="number-cell">₪${formatCurrency(
          dataToExport.reduce(
            (sum, inv) => sum + (parseFloat(inv.subTotal) || 0),
            0
          )
        )}</td>
        <td class="number-cell">₪${formatCurrency(
          dataToExport.reduce(
            (sum, inv) => sum + (parseFloat(inv.taxAmount) || 0),
            0
          )
        )}</td>
        <td class="number-cell">₪${formatCurrency(
          dataToExport.reduce(
            (sum, inv) => sum + (parseFloat(inv.totalAmount) || 0),
            0
          )
        )}</td>
      </tr>
    </tbody>
  </table>
  
  <div class="footer">
    ${t("generatedBy")} - ${new Date().toLocaleString(
      isRTL ? "he-IL" : "en-US"
    )}
  </div>
  
  <script>
    window.onload = function() {
      window.print();
      setTimeout(function() {
        window.close();
      }, 100);
    };
  </script>
</body>
</html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(htmlContent);
    printWindow.document.close();

    showModal(t("success"), t("printDialogOpened"), "success");
    setSelectionMode(false);
    setSelectedInvoices([]);
  };

  const showExportOptions = () => {
    setExportModalVisible(true);
  };

  const handleExportChoice = (format) => {
    setExportModalVisible(false);
    if (format === "excel") {
      exportToExcel();
    } else if (format === "pdf") {
      exportToPDF();
    }
  };

  const handleExpensePress = (expense) => {
    const invoice = invoices.find((inv) => inv.expensesId === expense.id);

    let message = `${t("date")}: ${formatDate(expense.date)}\n`;
    message += `${t("amount")}: ₪${formatCurrency(expense.price)}\n`;
    message += `${t("place")}: ${getPlaceName(expense.placeId)}\n`;
    message += `${t("withInvoice")}: ${
      expense.withInvoice === 1 ? t("yes") : t("no")
    }`;

    if (expense.note) {
      message += `\n${t("note")}: ${expense.note}`;
    }

    if (invoice) {
      message += `\n\n${t("invoiceDetails")}:`;
      message += `\n${t("invoiceNumber")}: ${invoice.inoviceNumber}`;
      message += `\n${t("vendorName")}: ${invoice.vendorName}`;
      message += `\n${t("taxRate")}: ${invoice.taxRate}%`;
    }

    showModal(t("expenseDetails"), message, "info", expense.image);
  };

  const openImageModal = (imageUri) => {
    setSelectedImage(imageUri);
    setImageModalVisible(true);
  };

  const closeImageModal = () => {
    setImageModalVisible(false);
    setSelectedImage(null);
  };

  const handleInvoicePress = (invoice) => {
    if (selectionMode) {
      toggleInvoiceSelection(invoice.id);
      return;
    }

    const associatedExpense = expenses.find(
      (exp) => exp.id === invoice.expensesId
    );

    let message = `${t("invoiceNumber")}: ${invoice.inoviceNumber}\n`;
    message += `${t("vendorName")}: ${invoice.vendorName}\n`;
    message += `${t("businessNumber")}: ${invoice.businessNumber}\n`;
    message += `${t("invoiceDate")}: ${invoice.inoviceDate}\n`;
    message += `${t("subTotal")}: ₪${formatCurrency(invoice.subTotal)}\n`;
    message += `${t("taxRate")}: ${invoice.taxRate}%\n`;
    message += `${t("taxAmount")}: ₪${formatCurrency(invoice.taxAmount)}\n`;
    message += `${t("totalAmount")}: ₪${formatCurrency(invoice.totalAmount)}\n`;
    message += `${t("place")}: ${getPlaceName(invoice.placeId)}`;

    showModal(
      t("invoiceDetails"),
      message,
      "info",
      associatedExpense?.image || null
    );
  };

  const renderExpenseCard = (item, index) => {
    const hasInvoice = item.withInvoice === 1;
    const invoice = invoices.find((inv) => inv.expensesId === item.id);

    return (
      <div
        key={item.id || index}
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          padding: "16px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
          border: "1px solid #F3F4F6",
          cursor: "pointer",
          transition: "all 0.2s ease",
          direction: isRTL ? "rtl" : "ltr",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={() => handleExpensePress(item)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = "0 8px 16px rgba(0, 0, 0, 0.12)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.08)";
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "12px",
            gap: "10px",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              backgroundColor: hasInvoice ? "#10B98115" : "#F59E0B15",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {hasInvoice ? (
              <Receipt size={20} color="#10B981" />
            ) : (
              <Wallet size={20} color="#F59E0B" />
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3
              style={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#111827",
                margin: 0,
                textAlign: isRTL ? "right" : "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getPlaceName(item.placeId)}
            </h3>
          </div>
        </div>

        {/* Amount */}
        <div
          style={{
            textAlign: "center",
            padding: "12px 0",
            marginBottom: "12px",
          }}
        >
          <span
            style={{
              fontSize: "24px",
              fontWeight: 700,
              color: "#111827",
            }}
          >
            ₪{formatCurrency(item.price)}
          </span>
        </div>

        {/* Date and Status */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "12px",
            gap: "8px",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#F3F4F6",
              padding: "4px 8px",
              borderRadius: "6px",
              gap: "4px",
              flex: 1,
              minWidth: 0,
            }}
          >
            <Calendar size={12} color="#6B7280" />
            <span
              style={{
                fontSize: "11px",
                color: "#6B7280",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {formatDate(item.date)}
            </span>
          </div>

          <div
            style={{
              padding: "4px 10px",
              borderRadius: "6px",
              backgroundColor: hasInvoice ? "#10B98120" : "#F59E0B20",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: "10px",
                fontWeight: 600,
                color: hasInvoice ? "#10B981" : "#F59E0B",
              }}
            >
              {hasInvoice ? "✓" : "✗"}
            </span>
          </div>
        </div>

        {/* Invoice Number if applicable */}
        {hasInvoice && invoice && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#10B98110",
              padding: "6px 10px",
              borderRadius: "6px",
              gap: "4px",
              marginBottom: "12px",
            }}
          >
            <CheckCircle size={12} color="#10B981" />
            <span
              style={{
                fontSize: "11px",
                color: "#10B981",
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {invoice.inoviceNumber}
            </span>
          </div>
        )}

        {/* Image Preview */}
        {item.image && (
          <div
            style={{
              marginTop: "auto",
              borderRadius: "8px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <img
              src={item.image}
              alt="Expense"
              style={{
                width: "100%",
                height: "140px",
                objectFit: "cover",
              }}
            />
            <button
              style={{
                position: "absolute",
                top: "6px",
                right: "6px",
                width: "28px",
                height: "28px",
                borderRadius: "14px",
                backgroundColor: "rgba(0,0,0,0.6)",
                border: "none",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                openImageModal(item.image);
              }}
            >
              <Maximize2 size={16} color="#FFF" />
            </button>
          </div>
        )}

        {/* Note */}
        {item.note && !item.image && (
          <div
            style={{
              marginTop: "auto",
              backgroundColor: "#F9FAFB",
              padding: "10px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "flex-start",
              gap: "6px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <FileText size={12} color="#6B7280" style={{ flexShrink: 0 }} />
            <p
              style={{
                flex: 1,
                fontSize: "11px",
                color: "#6B7280",
                margin: 0,
                lineHeight: "16px",
                textAlign: isRTL ? "right" : "left",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {item.note}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderInvoiceCard = (item, index) => {
    const isSelected = selectedInvoices.includes(item.id);
    const associatedExpense = expenses.find(
      (exp) => exp.id === item.expensesId
    );
    const expenseImage = associatedExpense?.image;

    return (
      <div
        key={item.id || index}
        style={{
          backgroundColor: isSelected ? "#EFF6FF" : "#FFFFFF",
          borderRadius: "12px",
          padding: "16px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
          border: isSelected ? "2px solid #3B82F6" : "1px solid #F3F4F6",
          cursor: "pointer",
          transition: "all 0.2s ease",
          position: "relative",
          direction: isRTL ? "rtl" : "ltr",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={() => handleInvoicePress(item)}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 8px 16px rgba(0, 0, 0, 0.12)";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.08)";
        }}
      >
        {/* Selection Checkbox */}
        {selectionMode && (
          <div
            style={{
              position: "absolute",
              top: "12px",
              right: isRTL ? "auto" : "12px",
              left: isRTL ? "12px" : "auto",
              zIndex: 10,
            }}
          >
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "12px",
                border: `2px solid ${isSelected ? "#3B82F6" : "#D1D5DB"}`,
                backgroundColor: isSelected ? "#3B82F6" : "#FFFFFF",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {isSelected && <CheckCircle size={14} color="#FFF" />}
            </div>
          </div>
        )}

        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "12px",
            gap: "10px",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              backgroundColor: "#3B82F615",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <FileText size={20} color="#3B82F6" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3
              style={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#111827",
                margin: "0 0 2px 0",
                textAlign: isRTL ? "right" : "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.inoviceNumber}
            </h3>
            <p
              style={{
                fontSize: "12px",
                color: "#6B7280",
                margin: 0,
                textAlign: isRTL ? "right" : "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.vendorName}
            </p>
          </div>
        </div>

        {/* Image Preview */}
        {expenseImage && (
          <div
            style={{
              marginBottom: "12px",
              borderRadius: "8px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <img
              src={expenseImage}
              alt="Invoice"
              style={{
                width: "100%",
                height: "140px",
                objectFit: "cover",
              }}
            />
            <button
              style={{
                position: "absolute",
                top: "6px",
                right: "6px",
                width: "28px",
                height: "28px",
                borderRadius: "14px",
                backgroundColor: "rgba(0,0,0,0.6)",
                border: "none",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                openImageModal(expenseImage);
              }}
            >
              <Maximize2 size={16} color="#FFF" />
            </button>
          </div>
        )}

        {/* Amount Breakdown */}
        <div
          style={{
            backgroundColor: "#F9FAFB",
            borderRadius: "8px",
            padding: "12px",
            marginBottom: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "6px",
              fontSize: "11px",
            }}
          >
            <span style={{ color: "#6B7280" }}>{t("subTotal")}:</span>
            <span style={{ fontWeight: 600, color: "#374151" }}>
              ₪{formatCurrency(item.subTotal)}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "6px",
              fontSize: "11px",
            }}
          >
            <span style={{ color: "#6B7280" }}>
              {t("tax")} ({item.taxRate}%):
            </span>
            <span style={{ fontWeight: 600, color: "#374151" }}>
              ₪{formatCurrency(item.taxAmount)}
            </span>
          </div>
          <div
            style={{
              height: "1px",
              backgroundColor: "#E5E7EB",
              margin: "8px 0",
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "13px",
            }}
          >
            <span style={{ fontWeight: 600, color: "#1E40AF" }}>
              {t("total")}:
            </span>
            <span style={{ fontWeight: 700, color: "#1E40AF" }}>
              ₪{formatCurrency(item.totalAmount)}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: "auto",
            paddingTop: "12px",
            borderTop: "1px solid #E5E7EB",
            display: "flex",
            justifyContent: "space-between",
            gap: "8px",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              flex: 1,
              minWidth: 0,
            }}
          >
            <MapPin size={12} color="#6B7280" style={{ flexShrink: 0 }} />
            <span
              style={{
                fontSize: "10px",
                color: "#6B7280",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getPlaceName(item.placeId)}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              flexShrink: 0,
            }}
          >
            <Calendar size={12} color="#6B7280" />
            <span style={{ fontSize: "10px", color: "#6B7280" }}>
              {item.inoviceDate}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from {
              transform: translateY(20px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>

      <div
        ref={containerRef}
        onScroll={handleScroll}
        style={{
          height: "100vh",
          overflowY: "auto",
          backgroundColor: "#F9FAFB",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        {/* Header Section */}
        <div
          style={{
            backgroundColor: "#FFFFFF",
            padding: "24px",
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <h1
            style={{
              fontSize: "28px",
              fontWeight: 700,
              color: "#111827",
              margin: "0 0 8px 0",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {contractorName}
          </h1>
          <p
            style={{
              fontSize: "14px",
              color: "#6B7280",
              margin: 0,
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {t("expensesAndInvoices")}
          </p>
        </div>

        {/* Stats Grid */}
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            padding: "24px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            {/* Total Expenses */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid #E5E7EB",
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 0, 0, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    backgroundColor: "#3B82F615",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Wallet size={24} color="#3B82F6" />
                </div>
              </div>
              <p
                style={{
                  fontSize: "13px",
                  color: "#6B7280",
                  margin: "0 0 8px 0",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("totalExpenses")}
              </p>
              <p
                style={{
                  fontSize: "28px",
                  fontWeight: 700,
                  color: "#111827",
                  margin: "0 0 4px 0",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                ₪{formatCurrency(getTotalExpenses())}
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#9CA3AF",
                  margin: 0,
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {expenses.length} {t("items")}
              </p>
            </div>

            {/* With Invoice */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid #E5E7EB",
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 0, 0, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    backgroundColor: "#10B98115",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CheckCircle size={24} color="#10B981" />
                </div>
              </div>
              <p
                style={{
                  fontSize: "13px",
                  color: "#6B7280",
                  margin: "0 0 8px 0",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("withInvoice")}
              </p>
              <p
                style={{
                  fontSize: "28px",
                  fontWeight: 700,
                  color: "#111827",
                  margin: "0 0 4px 0",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {getExpensesWithInvoice()}
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#9CA3AF",
                  margin: 0,
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("outOf")} {expenses.length}
              </p>
            </div>

            {/* Without Invoice */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid #E5E7EB",
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 0, 0, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    backgroundColor: "#F59E0B15",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <AlertCircle size={24} color="#F59E0B" />
                </div>
              </div>
              <p
                style={{
                  fontSize: "13px",
                  color: "#6B7280",
                  margin: "0 0 8px 0",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("noInvoice")}
              </p>
              <p
                style={{
                  fontSize: "28px",
                  fontWeight: 700,
                  color: "#111827",
                  margin: "0 0 4px 0",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {getExpensesWithoutInvoice()}
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#9CA3AF",
                  margin: 0,
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("needsAttention")}
              </p>
            </div>

            {/* Total Sub Total */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid #E5E7EB",
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 0, 0, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    backgroundColor: "#8B5CF615",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Calculator size={24} color="#8B5CF6" />
                </div>
              </div>
              <p
                style={{
                  fontSize: "13px",
                  color: "#6B7280",
                  margin: "0 0 8px 0",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("totalSubTotal")}
              </p>
              <p
                style={{
                  fontSize: "28px",
                  fontWeight: 700,
                  color: "#111827",
                  margin: "0 0 4px 0",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                ₪{formatCurrency(getTotalSubTotal())}
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#9CA3AF",
                  margin: 0,
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {invoices.length} {t("items")}
              </p>
            </div>

            {/* Total Tax */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid #E5E7EB",
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 0, 0, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    backgroundColor: "#EF444415",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Receipt size={24} color="#EF4444" />
                </div>
              </div>
              <p
                style={{
                  fontSize: "13px",
                  color: "#6B7280",
                  margin: "0 0 8px 0",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("totalTax")}
              </p>
              <p
                style={{
                  fontSize: "28px",
                  fontWeight: 700,
                  color: "#111827",
                  margin: "0 0 4px 0",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                ₪{formatCurrency(getTotalTax())}
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#9CA3AF",
                  margin: 0,
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("fromInvoices")}
              </p>
            </div>

            {/* Total Invoices */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid #E5E7EB",
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 0, 0, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    backgroundColor: "#06B6D415",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <FileText size={24} color="#06B6D4" />
                </div>
              </div>
              <p
                style={{
                  fontSize: "13px",
                  color: "#6B7280",
                  margin: "0 0 8px 0",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("totalInvoices")}
              </p>
              <p
                style={{
                  fontSize: "28px",
                  fontWeight: 700,
                  color: "#111827",
                  margin: "0 0 4px 0",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                ₪{formatCurrency(getTotalInvoices())}
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#9CA3AF",
                  margin: 0,
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {invoices.length} {t("items")}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              marginBottom: "24px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <button
              style={{
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "12px 24px",
                borderRadius: "10px",
                backgroundColor:
                  activeTab === "expenses" ? "#3B82F6" : "#FFFFFF",
                border: "1px solid #E5E7EB",
                cursor: "pointer",
                gap: "8px",
                transition: "all 0.2s ease",
              }}
              onClick={() => {
                setActiveTab("expenses");
                setSelectionMode(false);
                setSelectedInvoices([]);
                setSearchQuery("");
              }}
            >
              <Wallet
                size={18}
                color={activeTab === "expenses" ? "#FFF" : "#6B7280"}
              />
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: activeTab === "expenses" ? "#FFF" : "#6B7280",
                }}
              >
                {t("expenses")}
              </span>
              <div
                style={{
                  minWidth: "24px",
                  height: "24px",
                  borderRadius: "12px",
                  backgroundColor:
                    activeTab === "expenses" ? "#FFFFFF30" : "#F3F4F6",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "0 8px",
                }}
              >
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: activeTab === "expenses" ? "#FFF" : "#6B7280",
                  }}
                >
                  {expenses.length}
                </span>
              </div>
            </button>

            <button
              style={{
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "12px 24px",
                borderRadius: "10px",
                backgroundColor:
                  activeTab === "invoices" ? "#3B82F6" : "#FFFFFF",
                border: "1px solid #E5E7EB",
                cursor: "pointer",
                gap: "8px",
                transition: "all 0.2s ease",
              }}
              onClick={() => {
                setActiveTab("invoices");
                setSelectionMode(false);
                setSelectedInvoices([]);
              }}
            >
              <FileText
                size={18}
                color={activeTab === "invoices" ? "#FFF" : "#6B7280"}
              />
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: activeTab === "invoices" ? "#FFF" : "#6B7280",
                }}
              >
                {t("invoices")}
              </span>
              <div
                style={{
                  minWidth: "24px",
                  height: "24px",
                  borderRadius: "12px",
                  backgroundColor:
                    activeTab === "invoices" ? "#FFFFFF30" : "#F3F4F6",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "0 8px",
                }}
              >
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: activeTab === "invoices" ? "#FFF" : "#6B7280",
                  }}
                >
                  {invoices.length}
                </span>
              </div>
            </button>
          </div>

          {/* Search Bar and Actions for Invoices */}
          {activeTab === "invoices" && (
            <div style={{ marginBottom: "24px" }}>
              {/* Search Bar */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: "10px",
                  padding: "12px 16px",
                  marginBottom: "12px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <Search size={20} color="#9CA3AF" />
                <input
                  type="text"
                  style={{
                    flex: 1,
                    marginLeft: isRTL ? 0 : "12px",
                    marginRight: isRTL ? "12px" : 0,
                    fontSize: "14px",
                    color: "#111827",
                    border: "none",
                    backgroundColor: "transparent",
                    outline: "none",
                    textAlign: isRTL ? "right" : "left",
                  }}
                  placeholder={t("searchInvoices")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery.length > 0 && (
                  <button
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                    }}
                    onClick={() => setSearchQuery("")}
                  >
                    <X size={20} color="#9CA3AF" />
                  </button>
                )}
              </div>

              {/* Action Buttons */}
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <button
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 16px",
                    borderRadius: "10px",
                    backgroundColor: selectionMode ? "#FEE2E2" : "#EFF6FF",
                    border: "1px solid #E5E7EB",
                    cursor: "pointer",
                    gap: "6px",
                  }}
                  onClick={toggleSelectionMode}
                >
                  {selectionMode ? (
                    <X size={18} color="#EF4444" />
                  ) : (
                    <CheckCircle size={18} color="#3B82F6" />
                  )}
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: selectionMode ? "#EF4444" : "#3B82F6",
                    }}
                  >
                    {selectionMode ? t("cancel") : t("select")}
                  </span>
                </button>

                {selectionMode && (
                  <>
                    <button
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "10px 16px",
                        borderRadius: "10px",
                        backgroundColor: "#EFF6FF",
                        border: "1px solid #E5E7EB",
                        cursor: "pointer",
                        gap: "6px",
                      }}
                      onClick={
                        selectedInvoices.length === getFilteredInvoices().length
                          ? deselectAllInvoices
                          : selectAllInvoices
                      }
                    >
                      <CheckCircle size={18} color="#3B82F6" />
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#3B82F6",
                        }}
                      >
                        {selectedInvoices.length ===
                        getFilteredInvoices().length
                          ? t("deselectAll")
                          : t("selectAll")}
                      </span>
                    </button>

                    <button
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "10px 16px",
                        borderRadius: "10px",
                        backgroundColor: "#3B82F6",
                        border: "none",
                        cursor:
                          selectedInvoices.length === 0
                            ? "not-allowed"
                            : "pointer",
                        gap: "6px",
                        opacity: selectedInvoices.length === 0 ? 0.5 : 1,
                      }}
                      onClick={showExportOptions}
                      disabled={selectedInvoices.length === 0}
                    >
                      <Download size={18} color="#FFF" />
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#FFFFFF",
                        }}
                      >
                        {t("export")} ({selectedInvoices.length})
                      </span>
                    </button>
                  </>
                )}

                {!selectionMode && (
                  <button
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "10px 16px",
                      borderRadius: "10px",
                      backgroundColor: "#3B82F6",
                      border: "none",
                      cursor: "pointer",
                      gap: "6px",
                    }}
                    onClick={showExportOptions}
                  >
                    <Download size={18} color="#FFF" />
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#FFFFFF",
                      }}
                    >
                      {t("exportAll")}
                    </span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Content Grid */}
          {activeTab === "expenses" ? (
            expenses.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  paddingTop: "80px",
                  paddingBottom: "80px",
                }}
              >
                <div
                  style={{
                    width: "120px",
                    height: "120px",
                    borderRadius: "60px",
                    backgroundColor: "#F3F4F6",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: "24px",
                  }}
                >
                  <Wallet size={64} color="#D1D5DB" />
                </div>
                <h2
                  style={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "#374151",
                    textAlign: "center",
                    marginBottom: "8px",
                  }}
                >
                  {t("noExpensesFound")}
                </h2>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#9CA3AF",
                    textAlign: "center",
                    margin: 0,
                  }}
                >
                  {t("expensesWillAppearHere")}
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                  gap: "20px",
                  paddingBottom: "40px",
                }}
              >
                {expenses.map((item, index) => renderExpenseCard(item, index))}
              </div>
            )
          ) : getFilteredInvoices().length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                paddingTop: "80px",
                paddingBottom: "80px",
              }}
            >
              <div
                style={{
                  width: "120px",
                  height: "120px",
                  borderRadius: "60px",
                  backgroundColor: "#F3F4F6",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: "24px",
                }}
              >
                <FileText size={64} color="#D1D5DB" />
              </div>
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#374151",
                  textAlign: "center",
                  marginBottom: "8px",
                }}
              >
                {searchQuery ? t("noSearchResults") : t("noInvoicesFound")}
              </h2>
              <p
                style={{
                  fontSize: "14px",
                  color: "#9CA3AF",
                  textAlign: "center",
                  margin: 0,
                }}
              >
                {searchQuery
                  ? t("tryDifferentSearch")
                  : t("invoicesWillAppearHere")}
              </p>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                gap: "20px",
                paddingBottom: "40px",
              }}
            >
              {getFilteredInvoices().map((item, index) =>
                renderInvoiceCard(item, index)
              )}
            </div>
          )}
        </div>

        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button
            style={{
              position: "fixed",
              bottom: "32px",
              right: isRTL ? "auto" : "32px",
              left: isRTL ? "32px" : "auto",
              width: "56px",
              height: "56px",
              borderRadius: "28px",
              backgroundColor: "#3B82F6",
              border: "none",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              boxShadow: "0 8px 16px rgba(59, 130, 246, 0.4)",
              zIndex: 100,
              transition: "all 0.2s ease",
            }}
            onClick={scrollToTop}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <ArrowUp size={24} color="#FFF" />
          </button>
        )}

        {/* Image Modal */}
        {imageModalVisible && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.95)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
              animation: "fadeIn 0.2s ease-out",
            }}
            onClick={closeImageModal}
          >
            <button
              style={{
                position: "absolute",
                top: "24px",
                right: "24px",
                width: "48px",
                height: "48px",
                borderRadius: "24px",
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                border: "none",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                zIndex: 10,
                backdropFilter: "blur(10px)",
              }}
              onClick={closeImageModal}
            >
              <X size={28} color="#FFF" />
            </button>
            {selectedImage && (
              <img
                src={selectedImage}
                alt="Full size"
                style={{
                  maxWidth: "90%",
                  maxHeight: "90%",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
        )}

        {/* Custom Modal with Image */}
        {modalVisible && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10000,
              animation: "fadeIn 0.2s ease-out",
            }}
            onClick={() => setModalVisible(false)}
          >
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: "16px",
                padding: "32px",
                maxWidth: modalConfig.image ? "700px" : "500px",
                width: "90%",
                maxHeight: "80vh",
                overflow: "auto",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                animation: "slideUp 0.3s ease-out",
                direction: isRTL ? "rtl" : "ltr",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "24px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    flexDirection: isRTL ? "row-reverse" : "row",
                  }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "12px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor:
                        modalConfig.type === "success"
                          ? "#10B98115"
                          : modalConfig.type === "error"
                          ? "#EF444415"
                          : modalConfig.type === "warning"
                          ? "#F59E0B15"
                          : "#3B82F615",
                    }}
                  >
                    {modalConfig.type === "success" ? (
                      <CheckCircle size={28} color="#10B981" />
                    ) : modalConfig.type === "error" ? (
                      <X size={28} color="#EF4444" />
                    ) : modalConfig.type === "warning" ? (
                      <AlertCircle size={28} color="#F59E0B" />
                    ) : (
                      <Info size={28} color="#3B82F6" />
                    )}
                  </div>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: "22px",
                      fontWeight: 700,
                      color: "#1F2937",
                    }}
                  >
                    {modalConfig.title}
                  </h2>
                </div>
                <button
                  onClick={() => setModalVisible(false)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "8px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#F3F4F6";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <X size={24} color="#6B7280" />
                </button>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                  flexWrap: "wrap",
                }}
              >
                {/* Image */}
                {modalConfig.image && (
                  <div
                    style={{
                      flex: "0 0 auto",
                      width: modalConfig.image ? "200px" : "0",
                    }}
                  >
                    <img
                      src={modalConfig.image}
                      alt="Detail"
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: "300px",
                        objectFit: "cover",
                        borderRadius: "12px",
                        border: "2px solid #E5E7EB",
                        cursor: "pointer",
                      }}
                      onClick={() => openImageModal(modalConfig.image)}
                    />
                  </div>
                )}

                {/* Text Content */}
                <div
                  style={{
                    flex: 1,
                    minWidth: "200px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "15px",
                      color: "#4B5563",
                      lineHeight: "1.6",
                      whiteSpace: "pre-line",
                      textAlign: isRTL ? "right" : "left",
                    }}
                  >
                    {modalConfig.message}
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: "24px",
                  display: "flex",
                  justifyContent: "flex-end",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <button
                  onClick={() => setModalVisible(false)}
                  style={{
                    padding: "12px 32px",
                    borderRadius: "10px",
                    border: "none",
                    backgroundColor:
                      modalConfig.type === "success"
                        ? "#10B981"
                        : modalConfig.type === "error"
                        ? "#EF4444"
                        : modalConfig.type === "warning"
                        ? "#F59E0B"
                        : "#3B82F6",
                    color: "#FFFFFF",
                    fontSize: "15px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(0, 0, 0, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  {t("ok")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Export Options Modal */}
        {exportModalVisible && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10000,
              animation: "fadeIn 0.2s ease-out",
            }}
            onClick={() => setExportModalVisible(false)}
          >
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: "20px",
                padding: "32px",
                maxWidth: "450px",
                width: "90%",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                animation: "slideUp 0.3s ease-out",
                direction: isRTL ? "rtl" : "ltr",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "24px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    flexDirection: isRTL ? "row-reverse" : "row",
                  }}
                >
                  <div
                    style={{
                      width: "56px",
                      height: "56px",
                      borderRadius: "14px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#3B82F615",
                    }}
                  >
                    <Download size={32} color="#3B82F6" />
                  </div>
                  <div style={{ textAlign: isRTL ? "right" : "left" }}>
                    <h2
                      style={{
                        margin: 0,
                        fontSize: "22px",
                        fontWeight: 700,
                        color: "#1F2937",
                      }}
                    >
                      {t("exportOptions")}
                    </h2>
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "14px",
                        color: "#6B7280",
                      }}
                    >
                      {t("chooseExportFormat")}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setExportModalVisible(false)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "8px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#F3F4F6";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <X size={24} color="#6B7280" />
                </button>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <button
                  onClick={() => handleExportChoice("excel")}
                  style={{
                    padding: "20px",
                    borderRadius: "14px",
                    border: "2px solid #E5E7EB",
                    backgroundColor: "#FFFFFF",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    flexDirection: isRTL ? "row-reverse" : "row",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#10B981";
                    e.currentTarget.style.backgroundColor = "#10B98108";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 16px rgba(16, 185, 129, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#E5E7EB";
                    e.currentTarget.style.backgroundColor = "#FFFFFF";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div
                    style={{
                      width: "52px",
                      height: "52px",
                      borderRadius: "12px",
                      backgroundColor: "#10B98115",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <FileText size={28} color="#10B981" />
                  </div>
                  <div style={{ flex: 1, textAlign: isRTL ? "right" : "left" }}>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "#1F2937",
                      }}
                    >
                      Excel (XLSX)
                    </h3>
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "13px",
                        color: "#6B7280",
                      }}
                    >
                      {t("excelDescription")}
                    </p>
                  </div>
                  <ChevronRight
                    size={24}
                    color="#9CA3AF"
                    style={{
                      transform: isRTL ? "rotate(180deg)" : "none",
                    }}
                  />
                </button>

                <button
                  onClick={() => handleExportChoice("pdf")}
                  style={{
                    padding: "20px",
                    borderRadius: "14px",
                    border: "2px solid #E5E7EB",
                    backgroundColor: "#FFFFFF",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    flexDirection: isRTL ? "row-reverse" : "row",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#EF4444";
                    e.currentTarget.style.backgroundColor = "#EF444408";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 16px rgba(239, 68, 68, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#E5E7EB";
                    e.currentTarget.style.backgroundColor = "#FFFFFF";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div
                    style={{
                      width: "52px",
                      height: "52px",
                      borderRadius: "12px",
                      backgroundColor: "#EF444415",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <FileText size={28} color="#EF4444" />
                  </div>
                  <div style={{ flex: 1, textAlign: isRTL ? "right" : "left" }}>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "#1F2937",
                      }}
                    >
                      HTML/PDF
                    </h3>
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "13px",
                        color: "#6B7280",
                      }}
                    >
                      {t("pdfDescription")}
                    </p>
                  </div>
                  <ChevronRight
                    size={24}
                    color="#9CA3AF"
                    style={{
                      transform: isRTL ? "rotate(180deg)" : "none",
                    }}
                  />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
